import { useApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Divider,
  EmptyStation,
  formatDateTime,
  FormStation,
  getFormDiff,
  InfoPanel,
  type InitialFormData,
  MessageBar,
  type ObjectSchemaDefinition,
  Paragraph,
  RadioField,
  ReadOnlyField,
  Section,
  SingleLineTextField,
  type StationMessage,
} from '@axinom/mosaic-ui';
import { Field, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import type { OptionalObjectSchema } from 'yup/lib/object';
import { config } from '../../../../config';
import {
  IdentityProvider,
  type IdpConfiguration,
  type IdpConfigurationsQuery,
  useCreateIdpConfigurationMutation,
  useIdpConfigurationsQuery,
  useUpdateIdpConfigurationMutation,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';

interface FormData {
  id?: string;
  idpId?: IdentityProvider;
  discoveryDocumentUrl?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  clientSecretIsSet?: boolean;
  scopes?: (string | null)[];
}

type IdpConfigurationData = NonNullable<
  IdpConfigurationsQuery['configurations']
>['nodes'][number];

const getIdpConfigurationSchema = (
  isNewRecord: boolean,
): OptionalObjectSchema<ObjectSchemaDefinition<FormData>> => {
  const clientSecretSchema = Yup.string().label('Client Secret').max(255);
  const enabledSchema = Yup.boolean().label('IDP Status').required();
  const requiredSchema = Yup.string().required();

  return Yup.object<ObjectSchemaDefinition>({
    enabled: isNewRecord ? undefined : enabledSchema,
    clientId: Yup.string().label('Client ID').required().max(255),
    clientSecret: isNewRecord
      ? clientSecretSchema.concat(requiredSchema)
      : clientSecretSchema,
  });
};

export const IdpAxAuthDetail: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();
  const apolloClient = useApolloClient();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  // Check the status of axauth-service
  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.AXAUTH_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  const { loading, data, error } = useIdpConfigurationsQuery({
    client: apolloClient,
    variables: {
      idpConfigurationCondition: {
        environmentId: envId,
        idpId: IdentityProvider.AxAuth,
      },
      idpScopeFilter: {
        idpId: {
          equalTo: IdentityProvider.AxAuth,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const [createIdpConfig] = useCreateIdpConfigurationMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const [updateIdpConfig] = useUpdateIdpConfigurationMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const initialData = data?.configurations?.nodes[0];
  const detailObj = initialData || {
    environmentId: envId,
    discoveryDocumentUrl: new URL(
      '.well-known/openid-configuration',
      config.axAuthOidcBaseUrl ?? '',
    ).href,
    clientId: '',
    clientSecret: '',
  };

  const onSubmit = useCallback(
    async (
      formData: FormData,
      initialData: InitialFormData<FormData>,
    ): Promise<void> => {
      if (initialData.loading) {
        return;
      }

      if (initialData.data?.id !== undefined) {
        await updateIdpConfig({
          variables: {
            input: {
              id: initialData.data?.id,
              patch: createUpdateDto(formData, initialData.data),
            },
          },
        });
      } else {
        const requiredScopes: string[] = [];
        data?.scopes?.nodes?.forEach((scope) => {
          if (scope && scope.required) {
            requiredScopes.push(scope.scopeName);
          }
        });

        await createIdpConfig({
          variables: {
            input: {
              idpConfiguration: {
                environmentId: envId,
                idpId: IdentityProvider.AxAuth,
                discoveryDocumentUrl: formData.discoveryDocumentUrl,
                clientId: formData.clientId,
                clientSecret: formData.clientSecret ?? '',
                scopes: requiredScopes,
                sortOrder: 0,
              },
            },
          },
        });
      }
    },
    [createIdpConfig, data?.scopes?.nodes, envId, updateIdpConfig],
  );

  const [message] = useState<StationMessage>({
    title:
      'Certain Identity Providers may have different policies for automatically expiring Client Secrets. Please ensure if/when such expiration happens, the values stored here will be updated in a timely manner to provide an uninterrupted authentication experience.',
    type: 'info',
  });

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation
          title="Email and Password (AxAuth) Provider"
          subtitle="Configuration"
        >
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <FormStation<FormData>
          defaultTitle="Email and Password (AxAuth) Provider"
          subtitle="Configuration"
          validationSchema={getIdpConfigurationSchema(!initialData)}
          initialData={{
            data: detailObj,
            loading,
            error: error?.message,
          }}
          saveData={onSubmit}
          infoPanel={initialData ? <Panel /> : undefined}
          stationMessage={message}
        >
          <Form isNew={!initialData} />
        </FormStation>
      )}
    </>
  );
};

const Panel: React.FC = () => {
  const { values } = useFormikContext<IdpConfiguration>();

  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Last Modified">
          {formatDateTime(values.updatedAt)} by {values.updatedBy}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
        <Paragraph title="Environment ID">{values.environmentId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

const Form: React.FC<{
  isNew: boolean;
}> = ({ isNew }) => {
  const { values } = useFormikContext<IdpConfigurationData>();

  return (
    <>
      {!isNew && (
        <>
          <Field
            name="enabled"
            label="IDP Status"
            options={[
              { value: true, label: 'Enabled' },
              { value: false, label: 'Disabled' },
            ]}
            transform={(value: string) => JSON.parse(value)}
            as={RadioField}
          />
          <Divider />
        </>
      )}

      <Field
        name="clientId"
        label="Client ID"
        autoComplete="off"
        as={SingleLineTextField}
      />
      <Field
        name="clientSecret"
        label="Client Secret"
        type="password"
        autoComplete="one-time-code"
        isSet={values?.clientSecretIsSet}
        as={SingleLineTextField}
      />
      <Field
        name="discoveryDocumentUrl"
        label="Discovery Document URL"
        autoComplete="off"
        as={ReadOnlyField}
      />
    </>
  );
};

function createUpdateDto(
  currentValues: FormData,
  initialValues: FormData | null | undefined,
): Partial<FormData> {
  const updateData = getFormDiff(currentValues, initialValues);
  delete updateData['id'];
  return updateData;
}

export const AxAuthProviderDetailCrumb: BreadcrumbResolver = () => 'AxAuth IDP';
